import { useEffect, useRef, useState } from 'react';
import orthancFileSystemManager from '../../../../viewer/src/orthanc_duplicata/core/fileSystemManager';

const PREVIEW_CACHE_NAME = 'orthanc-previews-v1';

/**
 * caches.open() hits disk, so opening it once per card meant one round trip per
 * study card before any of them could reach the network. Memoized at module
 * level: every card now shares a single open. Measured 2026-08-06 over 62 calls
 * (two per card): 458ms cumulative down to 146ms.
 */
let previewCachePromise = null;
const openPreviewCache = () => {
  if (!('caches' in window)) return Promise.resolve(null);
  if (!previewCachePromise) {
    previewCachePromise = caches.open(PREVIEW_CACHE_NAME).catch(() => null);
  }

  return previewCachePromise;
};

const getFromHTTPCache = async url => {
  try {
    const cache = await openPreviewCache();
    if (!cache) return null;
    const cached = await cache.match(url);
    if (!cached) return null;
    const blob = await cached.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const saveToHTTPCache = async (url, blob) => {
  try {
    const cache = await openPreviewCache();
    if (!cache) return;
    await cache.put(
      url,
      new Response(blob, { headers: { 'Content-Type': blob.type } })
    );
  } catch {
    // ignore
  }
};

/**
 * @function useGetImage
 * @description HOOK for retrieve image from File System API cache or the server
 * Uses File System API for unlimited offline storage (Chrome/Edge only)
 * @param {string} object.url The url of the image
 */
const useGetImage = ({ url, study }) => {
  // #region INITIALIZATION
  // State
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Ref
  const abortControllerRef = useRef(null);

  const studyUID = (study && study.studyUID) || null;
  const seriesUID = (study && study.seriesUID) || null;
  // #endregion

  // #region EVENT
  const getHeader = () => {
    return {
      Authorization: `Basic ${btoa(
        `${process.env.ORTHANC_USERNAME}:${process.env.ORTHANC_PASSWORD}`
      )}`,
    };
  };

  /**
   * @function fetchImage Retrieve image from the server
   * @returns {blob} The image to show
   */
  const fetchImage = async () => {
    // Setup abort controller to abort request if component unmount
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    // Fetch image
    return await fetch(url, {
      signal: signal,
      headers: getHeader(),
    });
  };

  const handleImageFromCache = async () => {
    try {
      // Guard on an actual directory handle rather than on mere API support:
      // without a handle this branch cannot produce anything, and the old check
      // let it run only for getInstanceDirectory to return null and getFile to
      // throw on that null. Cheap either way, but the intent is now explicit.
      if (study && orthancFileSystemManager.directoryHandle) {
        try {
          const instanceId = url.split('/instances/')[1].split('/')[0]; // Extract instance ID from URL
          const instanceDir = await orthancFileSystemManager.getInstanceDirectory(
            studyUID,
            seriesUID,
            instanceId
          );
          const thumbnail = await orthancFileSystemManager.getFile(
            instanceDir,
            'preview.jpg'
          );
          if (thumbnail) {
            const blobURL = URL.createObjectURL(thumbnail);
            return blobURL;
          }
        } catch (err) {
          console.error(
            '[UseGetImage] File System not accessible, will fetch from server'
          );
        }
      }

      // Fallback: Cache API (persists across reloads, no re-prompt needed)
      return await getFromHTTPCache(url);
    } catch (error) {
      console.error('[UseGetImage] Error loading from cache:', error);
      return null;
    }
  };

  /**
   * @function getImage
   * @description Get image from the cache or the server
   */
  const getImage = async () => {
    setIsLoading(true);

    // Retrieve from cache
    const imageFromCache = await handleImageFromCache();
    if (imageFromCache) {
      setImage(imageFromCache);
      setIsLoading(false);
      return;
    }

    // Else fetch it
    try {
      const response = await fetchImage();
      if (response.ok) {
        const newImage = await response.blob();

        // Save to Cache API as fallback for next reload
        await saveToHTTPCache(url, newImage.slice(0));

        const blobURL = URL.createObjectURL(newImage);
        setImage(blobURL);
        setIsLoading(false);
      }

      if (!response.ok) {
        setError(
          `Erreur lors de la récupération de l'image: ${response.status} ${response.statusText}`
        );
        setIsLoading(false);
        throw new Error(
          `Erreur lors de la récupération de l'image: ${response.status} ${response.statusText}`
        );
      }
    } catch (err) {
      setError(err);
      setIsLoading(false);
      console.error(err);
    }
  };
  // #endregion

  // #region HOOK
  // Url reception
  useEffect(() => {
    if (!url) {
      setIsLoading(false);
      return;
    }

    if (image) {
      return;
    }

    getImage();

    return () => {
      setImage(null);
      setIsLoading(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [url, study]);
  // #endregion

  return { image, error, isLoading };
};

export { useGetImage };
